import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import {
  IconBarrierBlock,
  IconCalendarMonth,
  IconClockPlus,
  IconCurrencyDollar,
  IconExclamationCircle,
  IconUserCircle,
} from '@tabler/icons-react';
import { countBy } from 'lodash';
import React from 'react';
import { ActivityJobDto, PostDto } from 'src/api/FiretrolApiClient';
import { ViewActionIcon } from 'src/design/atoms/ViewActionIcon';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';

interface JobTableProps {
  jobs: ActivityJobDto[];
  loading: boolean;
  onSelectedJobChange: (
    details: { id: number; jobNumber: string } | null,
  ) => void;
}

export const JobTable: React.FC<JobTableProps> = ({
  jobs,
  loading,
  onSelectedJobChange,
}) => {
  const columns = React.useMemo(
    () => [
      {
        accessorKey: 'notifications',
        header: 'Alerts',
        size: 50,
        Cell: ({ row }) => {
          if (row.original.notifications?.length > 0) {
            return (
              <Badge color="red" size="sm" variant="filled">
                {row.original.notifications.length}
              </Badge>
            );
          }
          return null;
        },
      },
      {
        accessorKey: 'jobNumber',
        header: 'Job Number',
        size: 150,
      },
      {
        accessorKey: 'name',
        header: 'Job Name',
        size: 250,
      },
      {
        id: 'flags',
        header: '',
        size: 200,
        Cell: ({ row }) => {
          const hasConstraint = !!row.original.posts.find(
            (p: PostDto) => p.constraint && !p.resolved,
          );
          const hasCTC = !!row.original.posts.find(
            (p: PostDto) => p.ctc && !p.resolved,
          );
          const hasCritical = !!row.original.posts.find(
            (p: PostDto) => p.critical && !p.resolved,
          );
          const hasCost = !!row.original.posts.find(
            (p: PostDto) => p.cost && !p.resolved,
          );
          const unresolved = countBy(
            row.original.posts,
            (p: PostDto) => p.resolved === false,
          );

          const hasPerson = !!row.original.posts.find(
            (p: PostDto) => p.assignedToId !== null && !p.resolved,
          );

          const hasDate = !!row.original.posts.find(
            (p: PostDto) => p.date !== null && !p.resolved,
          );

          return (
            <Group gap="xs" justify="flex-end">
              {unresolved.true && (
                <Badge color="blue" size="sm">
                  {unresolved.true}
                </Badge>
              )}
              {hasConstraint && (
                <IconBarrierBlock
                  size={20}
                  color="var(--mantine-color-blue-6)"
                />
              )}
              {hasCTC && (
                <IconCurrencyDollar
                  size={20}
                  color="var(--mantine-color-blue-6)"
                />
              )}
              {hasCritical && (
                <IconExclamationCircle
                  size={20}
                  color="var(--mantine-color-blue-6)"
                />
              )}
              {hasCost && (
                <IconClockPlus size={20} color="var(--mantine-color-blue-6)" />
              )}
              {hasDate && (
                <IconCalendarMonth
                  size={20}
                  color="var(--mantine-color-blue-6)"
                />
              )}
              {hasPerson && (
                <IconUserCircle size={20} color="var(--mantine-color-blue-6)" />
              )}
            </Group>
          );
        },
      },
    ],
    [],
  );

  const tableState = useTableState({
    identifier: 'job-activity-table',
    columns,
  });

  const tableExports = useTableExports<ActivityJobDto>(
    'job-activity',
    (data) => {
      return data.map((item) => ({
        ['Alerts']: item.notifications?.length || 0,
        ['Job Number']: item.jobNumber,
        ['Job Name']: item.name,
        ['Open Assignments']:
          item.posts?.filter((p) => !p.resolved).length || 0,
      }));
    },
  );

  const table = useTable({
    ...tableState,
    tableOptions: {
      data: jobs,
      columns,
      enableRowActions: true,
      renderRowActions: ({ row }) => (
        <ViewActionIcon
          onClick={() =>
            onSelectedJobChange({
              id: row.original.id,
              jobNumber: row.original.jobNumber || '',
            })
          }
        />
      ),
    },
    totalCount: jobs.length,
    isLoading: loading,
    isFetching: false,
    isError: false,
    ...tableExports,
  });

  return <>{table.table}</>;
};
